import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { modulesApi, Module } from '@/api/modules';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Eye, Package, Search, Server, Smartphone } from 'lucide-react';

type TabType = 'v2' | 'v1';

export function ModulesPage() {
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('v2');

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await modulesApi.getAll();
      setModules(data);
    } catch (err) {
      setError('Erreur lors du chargement des modules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Separate V1 and V2 modules
  const v1Modules = modules.filter((m) => !m.isV2);
  const v2Modules = modules.filter((m) => m.isV2);

  const currentModules = activeTab === 'v2' ? v2Modules : v1Modules;

  const filteredModules = useMemo(() => {
    if (!searchValue) return currentModules;
    const search = searchValue.toLowerCase();
    return currentModules.filter(
      (m) =>
        m.code.toLowerCase().includes(search) ||
        m.name.toLowerCase().includes(search)
    );
  }, [currentModules, searchValue]);

  const formatPrice = (price: number | null) => {
    if (price === null) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Package className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Modules
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gérez les modules disponibles pour les écoles V1 et V2
          </p>
        </div>

      </div>

      {/* Tabs */}
      <div>
        <nav className="flex gap-1">
          <button
            onClick={() => setActiveTab('v2')}
            className={`flex items-center gap-2 py-2.5 px-4 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === 'v2'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <Server className="h-4 w-4" />
            Modules V2
            <Badge variant={activeTab === 'v2' ? 'outline' : 'secondary'} className={activeTab === 'v2' ? 'bg-white/20 text-white border-white/30' : ''}>
              {v2Modules.length}
            </Badge>
          </button>
          <button
            onClick={() => setActiveTab('v1')}
            className={`flex items-center gap-2 py-2.5 px-4 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === 'v1'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <Smartphone className="h-4 w-4" />
            Modules V1
            <Badge variant={activeTab === 'v1' ? 'outline' : 'secondary'} className={activeTab === 'v1' ? 'bg-white/20 text-white border-white/30' : ''}>
              {v1Modules.length}
            </Badge>
          </button>
        </nav>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* Tab description */}
      {activeTab === 'v2' && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
          <p>
            Les modules V2 sont utilises dans les offres commerciales (MVP, BASIC, EXPERT).
            Les prix sont définis par offre et par tranche d'élèves.
          </p>
        </div>
      )}
      {activeTab === 'v1' && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
          <p>
            Les modules V1 sont associés directement aux écoles.
            Les prix sont definis par année fiscale avec 4 tranches.
          </p>
        </div>
      )}

      {/* Search and count */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher par code ou nom..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="pl-9"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          {filteredModules.length} module{filteredModules.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
          Chargement...
        </div>
      ) : filteredModules.length === 0 ? (
        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
          Aucun module trouve.
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden bg-card border">
          {activeTab === 'v2' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredModules.map((module) => (
                  <TableRow key={module.id}>
                    <TableCell>
                      <span className="font-medium text-gray-900">{module.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm text-gray-600">
                        {module.code}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => navigate(`/modules/${module.code}`)}
                          title="Voir"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead className="text-center">Écoles</TableHead>
                  <TableHead className="text-center">Prix annuel / élève</TableHead>
                  <TableHead className="w-[120px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredModules.map((module) => (
                  <TableRow key={module.id}>
                    <TableCell>
                      <span className="font-medium text-gray-900">{module.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm text-gray-600">
                        {module.code}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="font-mono">
                        {module.schoolCount}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-gray-600">{formatPrice(module.currentPrice)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => navigate(`/modules/${module.code}`)}
                          title="Voir"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

    </div>
  );
}
